import React, { Suspense, memo, useEffect } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import { ConfigProvider, App, theme as antdTheme } from 'antd'
import { IntlProvider } from 'react-intl'
import AppLocale from 'lang'
import useBodyClass from 'hooks/useBodyClass'
import Loading from 'components/shared-components/Loading'
import ModalBridgeRegistrar from 'components/util-components/ModalBridgeRegistrar'
import Routes from 'routes'
import TxtFileService from 'services/TxtFileService'
import { saveAdminSettings } from 'store/slices/authSlice'
import {
  applyTinyMceKeyFromSettings,
  injectTinyMceCdn,
} from 'utils/tinymceBootstrap'

const Layouts = ({ locale, direction, currentTheme }) => {
  const dispatch = useDispatch()
  const adminSettings = useSelector((state) => state.auth.adminSettings)
  const currentAppLocale = AppLocale[locale]

  useBodyClass(`dir-${direction}`)

  useEffect(() => {
    injectTinyMceCdn()
  }, [adminSettings])

  useEffect(() => {
    const fetchAdminSettings = async () => {
      try {
        const { data } = await TxtFileService.getTxtFileLinks()
        if (!data?.generalTxt) return

        const generalTxtUrl = `${data.generalTxt}${
          data.generalTxt.includes('?') ? '&' : '?'
        }_=${Date.now()}`
        const generalData = await fetch(generalTxtUrl).then((res) => res.json())
        if (!generalData) return

        const settings = generalData?.adminSettings?.[0] || null
        dispatch(saveAdminSettings(settings))
        applyTinyMceKeyFromSettings(settings)
        injectTinyMceCdn()
      } catch (e) {
        console.error(e)
      }
    }
    fetchAdminSettings()
  }, [dispatch])

  const antdAlgorithm =
    currentTheme === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm
  const isDark = currentTheme === 'dark'
  const tokenTheme = {
    colorPrimary: '#3e79f7',
    colorSuccess: '#04d182',
    colorWarning: '#ffc542',
    colorError: '#ff6b72',
    colorInfo: '#3e79f7',
    colorText: isDark ? '#d6deeb' : '#455560',
    colorTextHeading: isDark ? '#f2f5fb' : '#1a3353',
    colorTextSecondary: isDark ? '#9aa8c4' : '#72849a',
    colorBgContainer: isDark ? '#1f2737' : '#ffffff',
    colorBgLayout: isDark ? '#111827' : '#fafafb',
    colorBorder: isDark ? '#344158' : '#e6ebf1',
    borderRadius: 10,
    borderRadiusSM: 4,
    borderRadiusLG: 12,
    controlHeight: 40,
    fontSize: 14,
    fontSizeSM: 12,
    fontSizeLG: 16,
    lineHeight: 1.5,
    fontFamily:
      "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    boxShadowSecondary:
      '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
  }

  const components = {
    Button: {
      borderRadius: 10,
      controlHeight: 40,
      controlHeightSM: 36,
      controlHeightLG: 48,
      contentFontSize: 14,
      contentLineHeight: 1.5,
      defaultBorderColor: tokenTheme.colorBorder,
      defaultColor: tokenTheme.colorText,
      defaultBg: tokenTheme.colorBgContainer,
      primaryShadow: 'none',
      defaultShadow: 'none',
    },
    Input: {
      borderRadius: 10,
      controlHeight: 40,
      controlHeightSM: 36,
      controlHeightLG: 48,
      activeBorderColor: tokenTheme.colorPrimary,
      hoverBorderColor: tokenTheme.colorPrimary,
      colorBgContainer: tokenTheme.colorBgContainer,
    },
    Select: {
      borderRadius: 10,
      optionSelectedBg: 'rgba(62, 121, 247, 0.1)',
      optionActiveBg: 'rgba(62, 121, 247, 0.1)',
      optionSelectedColor: tokenTheme.colorPrimary,
      colorBgContainer: tokenTheme.colorBgContainer,
    },
    Menu: {
      itemHeight: 40,
      itemBorderRadius: 8,
      itemColor: tokenTheme.colorText,
      itemSelectedColor: tokenTheme.colorPrimary,
      itemSelectedBg: 'rgba(62, 121, 247, 0.1)',
      itemActiveBg: 'rgba(62, 121, 247, 0.1)',
    },
    Tabs: {
      itemColor: tokenTheme.colorText,
      itemSelectedColor: tokenTheme.colorPrimary,
      itemActiveColor: tokenTheme.colorPrimary,
      cardHeight: 40,
      inkBarColor: tokenTheme.colorPrimary,
    },
    Table: {
      headerBg: isDark ? tokenTheme.colorBgContainer : '#f7f7f8',
      headerColor: tokenTheme.colorTextHeading,
      rowHoverBg: isDark ? 'rgba(255,255,255,0.04)' : '#f7f7f8',
      borderColor: tokenTheme.colorBorder,
      headerBorderRadius: 10,
      fontSize: 14,
    },
    Modal: {
      borderRadiusLG: 10,
      titleColor: tokenTheme.colorTextHeading,
      titleFontSize: 16,
      contentBg: tokenTheme.colorBgContainer,
      headerBg: tokenTheme.colorBgContainer,
      footerBg: 'transparent',
    },
    Drawer: {
      colorBgElevated: tokenTheme.colorBgContainer,
      colorText: tokenTheme.colorText,
      footerPaddingBlock: 10,
      footerPaddingInline: 16,
    },
    Pagination: {
      itemSize: 30,
      itemSizeSM: 24,
      itemActiveBg: tokenTheme.colorPrimary,
      itemActiveColor: '#fff',
      itemBg: 'transparent',
      colorText: tokenTheme.colorText,
      borderRadius: 9999,
    },
    Card: {
      borderRadiusLG: 10,
      colorBorderSecondary: tokenTheme.colorBorder,
      headerBg: 'transparent',
      headerHeight: 52,
      bodyPadding: 20,
    },
    Form: {
      labelColor: tokenTheme.colorTextHeading,
      itemMarginBottom: 24,
    },
  }

  return (
    <IntlProvider
      locale={currentAppLocale.locale}
      messages={currentAppLocale.messages}
    >
      <ConfigProvider
        locale={currentAppLocale.antd}
        direction={direction}
        theme={{
          algorithm: antdAlgorithm,
          token: tokenTheme,
          components,
        }}
      >
        <App>
          <ModalBridgeRegistrar />
          <Suspense fallback={<Loading cover="page" />}>
            <Routes />
          </Suspense>
        </App>
      </ConfigProvider>
    </IntlProvider>
  )
}

const mapStateToProps = ({ theme }) => {
  const { locale, direction, currentTheme } = theme
  return { locale, direction, currentTheme }
}

export default connect(mapStateToProps)(memo(Layouts))
